import { useState, useRef } from "react";
import Button from "../../../components/Button";
import { useAuth } from "../../../contexts/AuthContext";
import { useLocation } from "react-router";
import { useTranslation } from "../../../i18n/TranslationProvider";
import { environment } from "../../../api/apiConfig";

const RosterUpload = () => {
   const [selectedFile, setSelectedFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);
    const { state } = useLocation();
    const { userId: authUserId } = useAuth();
    const userId = state?.userId ?? authUserId;
    const [email, setEmail] = useState("");
    const [agreed, setAgreed] = useState(false);
    const { t } = useTranslation();

    const SPREADSHEET_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
    "text/csv",                 // .csv
    ];

    const SPREADSHEET_EXTENSIONS = /\.(xlsx|xls|csv)$/i;

    const clearFile = () => {
        setSelectedFile(null);
        setError("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleFileSelect = (file) => {
        setError("");
        const validType = SPREADSHEET_TYPES.includes(file.type);
        const validExt = SPREADSHEET_EXTENSIONS.test(file.name);
        if (!validType && !validExt) {
            setError(t("athletes.spreadsheetFileTypeError"));
            return;
        }
        setSelectedFile(file);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setError(t("athletes.noFileSelectedError"));
            return;
        }
        handleFileSelect(file);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        setDragActive(true);
    };
    const onDragOver = (e) => {
        e.preventDefault();
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        setDragActive(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (selectedFile) return;
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            setError(t("athletes.selectFileBeforeSubmitError"));
            return;
        }

        // Validate file extension and MIME type
        const allowedExtensions = ["csv", "xls", "xlsx"];
        const allowedMimeTypes = [
            "text/csv",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];
        const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

        if (
            !allowedExtensions.includes(fileExtension) ||
            !allowedMimeTypes.includes(selectedFile.type)
        ) {
            setError(t("athletes.onlyCsvXlsXlsxAllowed"));
            return;
        }

        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            setError(t("athletes.pleaseEnterValidEmail"));
            return;
        }

        if (!agreed) {
            setError(t("athletes.mustAgreeToTermsError"));
            return;
        }

        setSubmitting(true);
        setError("");

        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const base64Data = event.target.result.split(",")[1];

                const body = new URLSearchParams();
                body.append("file", base64Data);
                body.append("fileName", selectedFile.name);
                body.append("mimeType", selectedFile.type);
                body.append("userID", userId);
                body.append("email", email);
                body.append("environment", environment);

                const response = await fetch(
                    //"https://script.google.com/macros/s/AKfycbw_Ga6Vrrohgl8CVfpfC_9vF0AEgYa9OvnIjApxIfSM7LghimEkciZlVkn3J0_2z8PX/exec"
                    "https://script.google.com/macros/s/AKfycbxseXBX8zz-yPh57yzCtx6fdVEMN4cdG6Izv_b2ttrZqPZAV7rWOe5UDKF6HHxUm8Gl/exec",
                    {
                        method: "POST",
                        body,
                    }
                );

                const result = await response.json();

                if (result.success) {
                    setSubmitted(true);
                } else {
                    setError(result.error || t("common.somethingWentWrongGeneric"));
                }
                setSubmitting(false);
            }; 

            reader.onerror = () => {
                setSubmitting(false);
                setError(t("athletes.fileReadError"));
            };

            reader.readAsDataURL(selectedFile);
        } catch (err) {
            setSubmitting(false);
            setError(t("athletes.uploadFailedError"));
            console.error(err);
        }
    };

    if (submitted) {
        return (
            <div>
                <p className="text-teal-600 text-center ">
                    {t("athletes.rosterUploadSuccessLine1")}
                    <br />
                    {t("athletes.rosterUploadSuccessLine2")}
                    <br />
                    {t("athletes.rosterUploadSuccessLine3")}
                </p>
            </div>
        );
    }
    return (
        <form onSubmit={handleSubmit}>
            <div
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                <label
                    htmlFor="file-upload"
                    className={`relative flex flex-col items-center justify-center
              border-2 border-dashed rounded-lg p-8 cursor-pointer text-center
              ${dragActive ? "border-primary bg-gray-300" : "border-gray-300"}`}
                >
                    {!selectedFile && (
                        <input
                            ref={fileInputRef}
                            id="file-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            disabled={Boolean(selectedFile) || submitting}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                    )}

                    {selectedFile ? (
                        <div className="flex flex-col items-center space-y-2">
                            <span className="font-medium">
                                {selectedFile.name}
                            </span>
                            <span className="text-sm text-gray-500">
                                {selectedFile.size > 1024 * 1024
                                    ? (
                                          selectedFile.size /
                                          (1024 * 1024)
                                      ).toFixed(1) + " MB"
                                    : (selectedFile.size / 1024).toFixed(1) +
                                      " KB"}{" "}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearFile}
                            >
                                {t("athletes.uploadDifferentFileButton")}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center space-y-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="size-10"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>

                            <p className="text-gray-600">
                                {t("athletes.dragDropRosterHere")}
                                <br />
                                <span className="underline text-primary">
                                    {t("athletes.orClickToBrowse")}
                                </span>
                            </p>
                        </div>
                    )}
                </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
                {t("athletes.onlyCsvXlsXlsxAllowed")}
            </p>
            <div className="mt-4">
                <label
                    htmlFor="email"
                    className="block font-semibold text-black mb-1"
                >
                    {t("athletes.emailAddressLabel")}
                </label>
                <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    disabled={submitting}
                />
            </div>

            <div className="mt-4 flex items-start">
                <input
                    id="legal-agree"
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    disabled={submitting}
                    className="mr-2 mt-1"
                    required
                />
                <label htmlFor="legal-agree" className="text-gray-700 text-sm">
                    {t("athletes.uploadConsentText")}
                </label>
            </div>

            {/* Upload progress */}
            {/* TODO: add translations */}

            {submitting && (
                <div className="mt-4">
                    <div
                        style={{
                            fontSize: 12,
                            color: "#6c757d",
                            marginBottom: 4,
                        }}
                    >
                         {t("athletes.progressBar")}
                    </div>

                    <div
                        style={{
                            background: "#e9ecef",
                            borderRadius: 4,
                            height: 6,
                            overflow: "hidden",
                        }}
                    >
                        <div
                            className="roster-processing-bar"
                            style={{
                                height: "100%",
                                width: "40%",
                                background: "#106995",
                                borderRadius: 4,
                            }}
                        />
                    </div>
                </div>
            )}

            <Button
                type="submit"
                disabled={!selectedFile || !email || !agreed}
                isLoading={submitting}
                className="mt-4 w-full"
            >
                {submitting ? t("athletes.uploadingRosterButton") : t("athletes.uploadRosterPageHeading")}
            </Button>
            {error && <p className="text-red-600">{error}</p>}
        </form>
    );
};

export default RosterUpload;